import React from "react";
import { Link } from "react-router-dom";

export default function PrivacyPolicy() {
  const lastUpdated = "August 15, 2025";

  return (
    <main className="mx-auto max-w-3xl px-6 py-12">
      <header className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight text-slate-900">Privacy Policy</h1>
        <p className="mt-1 text-sm text-slate-500">Last updated {lastUpdated}</p>
      </header>

      {/* On this page */}
      <nav className="mb-8 rounded-xl border border-slate-200 bg-slate-50/60 p-4 text-sm text-slate-700">
        <p className="mb-2 font-semibold">On this page</p>
        <ul className="grid grid-cols-1 gap-1 sm:grid-cols-2">
          <li><a className="text-sky-700 hover:underline" href="#who-we-are">1. Who we are</a></li>
          <li><a className="text-sky-700 hover:underline" href="#scope">2. Scope</a></li>
          <li><a className="text-sky-700 hover:underline" href="#data-we-collect">3. Data we collect</a></li>
          <li><a className="text-sky-700 hover:underline" href="#how-we-use">4. How we use data</a></li>
          <li><a className="text-sky-700 hover:underline" href="#legal-bases">5. Legal bases</a></li>
          <li><a className="text-sky-700 hover:underline" href="#sharing">6. How we share</a></li>
          <li><a className="text-sky-700 hover:underline" href="#retention">7. Data retention</a></li>
          <li><a className="text-sky-700 hover:underline" href="#security">8. Security</a></li>
          <li><a className="text-sky-700 hover:underline" href="#cookies">9. Cookies</a></li>
          <li><a className="text-sky-700 hover:underline" href="#rights">10. Your rights</a></li>
          <li><a className="text-sky-700 hover:underline" href="#intl">11. International transfers</a></li>
          <li><a className="text-sky-700 hover:underline" href="#children">12. Children’s privacy</a></li>
          <li><a className="text-sky-700 hover:underline" href="#changes">13. Changes</a></li>
          <li><a className="text-sky-700 hover:underline" href="#contact">14. Contact</a></li>
        </ul>
      </nav>

      <section id="who-we-are" className="prose prose-slate max-w-none">
        <h2>1. Who we are</h2>
        <p>
          <strong>Frontera</strong> (“Frontera,” “we,” “us,” or “our”) provides tools to help you
          discover and collaborate on projects. This Privacy Policy explains how we collect, use,
          and share information about you when you use our websites, applications, and services
          (collectively, the “Services”).
        </p>
      </section>

      <section id="scope" className="prose prose-slate max-w-none">
        <h2>2. Scope</h2>
        <p>
          This Policy applies to personal information processed by Frontera in connection with the
          Services. It does not cover third-party websites or services that we do not control. We use our
          own built-in safeguards (like rate limits, device checks, and anomaly detection) to protect the Service.
        </p>
      </section>

      <section id="data-we-collect" className="prose prose-slate max-w-none">
        <h2>3. Data we collect</h2>
        <h3>Information you provide</h3>
        <ul>
          <li><strong>Account details</strong> such as first and last name, email, password.</li>
          <li><strong>Profile</strong> such as areas of interest, city, country, and optional profile photo.</li>
          <li><strong>Communications</strong> when you contact support or interact with notices.</li>
        </ul>
        <h3>Information collected automatically</h3>
        <ul>
          <li><strong>Usage data</strong> (pages viewed, actions taken, timestamps).</li>
          <li><strong>Device & diagnostics</strong> (browser type, OS, device identifiers, IP address).</li>
          <li><strong>Security signals</strong> (rate-limit counters, abuse indicators, error logs).</li>
        </ul>
        <h3>Information from others</h3>
        <p>
          We may receive information from service providers acting on our behalf (e.g., email
          delivery, cloud hosting). We do not buy or sell personal data.
        </p>
      </section>

      <section id="how-we-use" className="prose prose-slate max-w-none">
        <h2>4. How we use data</h2>
        <ul>
          <li>Provide, operate, and improve the Services.</li>
          <li>Authenticate you and maintain your account (including email verification).</li>
          <li>Personalize content (e.g., showing interests you add to your profile).</li>
          <li>Protect against fraud, abuse, and security threats using built-in safeguards (e.g., rate limits and device checks).</li>
          <li>Communicate with you (service announcements, replies to support).</li>
          <li>Comply with legal obligations and enforce our Terms.</li>
          <li>Create aggregated or de-identified insights that do not identify you.</li>
        </ul>
      </section>

      <section id="legal-bases" className="prose prose-slate max-w-none">
        <h2>5. Legal bases (EEA/UK)</h2>
        <ul>
          <li><strong>Contract</strong> – to provide the Services you request.</li>
          <li><strong>Legitimate interests</strong> – e.g., security and service improvement.</li>
          <li><strong>Consent</strong> – where required (you may withdraw at any time).</li>
          <li><strong>Legal obligation</strong> – to comply with applicable law.</li>
        </ul>
      </section>

      <section id="sharing" className="prose prose-slate max-w-none">
        <h2>6. How we share information</h2>
        <ul>
          <li><strong>Service providers</strong> under agreements that restrict their use to our instructions (e.g., cloud hosting, email delivery, analytics without advertising).</li>
          <li><strong>Legal</strong> – to comply with law, respond to lawful requests, or protect rights, safety, and security.</li>
          <li><strong>Business transfers</strong> – in connection with a merger, acquisition, or asset sale.</li>
          <li><strong>With your direction or consent</strong>.</li>
        </ul>
        <p><em>We do not sell or “share” personal information for cross-context behavioral advertising.</em></p>
      </section>

      <section id="retention" className="prose prose-slate max-w-none">
        <h2>7. Data retention</h2>
        <p>
          We keep personal information for as long as necessary to provide the Services, comply with
          legal obligations, resolve disputes, and enforce agreements. You may request deletion of
          your account at any time; we’ll also delete or anonymize data that we no longer need.
        </p>
      </section>

      <section id="security" className="prose prose-slate max-w-none">
        <h2>8. Security</h2>
        <p>
          We use industry-standard safeguards, including encryption in transit, hashed passwords, and
          access controls. No method of transmission or storage is 100% secure; we cannot guarantee
          absolute security.
        </p>
      </section>

      <section id="cookies" className="prose prose-slate max-w-none">
        <h2>9. Cookies & similar technologies</h2>
        <p>
          We use essential cookies and local storage to keep you signed in and operate the Service.
          We do not use third-party advertising cookies. You can control cookies in your browser
          settings, but the Service may not work properly without certain cookies.
        </p>
      </section>

      <section id="rights" className="prose prose-slate max-w-none">
        <h2>10. Your rights</h2>
        <p>
          Depending on your location, you may have rights to access, correct, delete, or export your
          data; to object to or restrict processing; and to withdraw consent where processing is
          based on consent. California residents may also request information under the CCPA/CPRA.
          To exercise rights, contact{" "}
          <a className="text-sky-700 hover:underline" href="mailto:support@frontera.app">
            support@frontera.app
          </a>.
        </p>
      </section>

      <section id="intl" className="prose prose-slate max-w-none">
        <h2>11. International data transfers</h2>
        <p>
          If we transfer personal information outside your country, we use appropriate safeguards
          (e.g., contractual terms) to protect it.
        </p>
      </section>

      <section id="children" className="prose prose-slate max-w-none">
        <h2>12. Children’s privacy</h2>
        <p>
          The Services are not directed to children under 13 (or older age as required by law).
          We do not knowingly collect personal information from children.
        </p>
      </section>

      <section id="changes" className="prose prose-slate max-w-none">
        <h2>13. Changes to this Policy</h2>
        <p>
          We may update this Policy from time to time. If we make material changes, we’ll notify you
          (for example, by email or in-app) and update the “Last updated” date above.
        </p>
      </section>

      <section id="contact" className="prose prose-slate max-w-none">
        <h2>14. Contact</h2>
        <p>
          Questions or requests? Email{" "}
          <a className="text-sky-700 hover:underline" href="mailto:support@frontera.app">
            support@frontera.app
          </a>.
        </p>
        <p className="text-xs text-slate-500 mt-6">
          This template is provided for convenience only and does not constitute legal advice.
        </p>
        <p className="text-xs text-slate-500">
          If you need a printable copy, you can{" "}
          <Link className="text-sky-700 hover:underline" to="/legal/privacy.pdf">download a PDF</Link>.
        </p>
      </section>
    </main>
  );
}
